import { CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useDebouncedCallback } from '../../../../../../hooks'
import ComponentDocsPanel from '../../ComponentDocsPanel'
import AgentSidePanel from '../../AgentSidePanel'
import { useRestoredThreadId } from '../../AgentSidePanel/hooks/useRestoredThreadId'
import { clearStoredThread, writeStoredThread, readStoredPanelWidth, writeStoredPanelWidth } from '../../AgentSidePanel/hooks/threadStorage'
import ComparePane from '../ComparePane'
import CodeEditorToolbar from './CodeEditorToolbar'
import ContentCodeView from './ContentCodeView'
import ContentPreviewView from './ContentPreviewView'
import ViewContentHeaderControls from './ViewContentHeaderControls'

const PANEL_MIN_WIDTH = 400
const PANEL_MAX_ABS_WIDTH = 800
const clampPanelWidth = (w) => {
  if (typeof window === 'undefined') return Math.max(PANEL_MIN_WIDTH, Math.min(w, PANEL_MAX_ABS_WIDTH))
  const max = Math.min(PANEL_MAX_ABS_WIDTH, Math.round(window.innerWidth * 0.33))
  return Math.max(PANEL_MIN_WIDTH, Math.min(w, max))
}

const ViewContentContainer = forwardRef(({
  jsxContent,
  viewId,
  onJsxContentChange,
  onDiagnosticsChange: onDiagnosticsChangeProp,
  toolbarLeft,
  isEdit,
  isComparing,
  onVersionCreated
}, ref) => {
  const codeViewRef = useRef(null)
  const agentPanelRef = useRef(null)

  const [viewMode, setViewMode] = useState('code')

  const handleViewModeChange = useCallback((mode) => {
    setViewMode(mode)
    if (mode === 'code') {
      setTimeout(() => {
        codeViewRef.current?.focus?.()
      }, 100)
    }
  }, [])

  const handleFind = useCallback(() => {
    codeViewRef.current?.find?.()
  }, [])

  const handleReplace = useCallback(() => {
    codeViewRef.current?.replace?.()
  }, [])

  const handleUndo = useCallback(() => {
    codeViewRef.current?.undo?.()
  }, [])

  const handleRedo = useCallback(() => {
    codeViewRef.current?.redo?.()
  }, [])

  const handleFormat = useCallback(async () => {
    await codeViewRef.current?.format?.()
  }, [])

  const views = [
    { id: 'code', title: 'Code View', icon: 'file', size: 'sm' },
    { id: 'preview', title: 'Preview', icon: 'visibility', size: 'sm' }
  ]

  const agentId = isEdit ? 'ae-view-editor' : 'ae-view-builder'
  const viewScope = isEdit ? (viewId ?? 'unknown') : 'new'

  const [docsComponentName, setDocsComponentName] = useState(null)
  const [aiDesignerActive, setAiDesignerActive] = useState(false)
  const [hasDesignerBeenOpened, setHasDesignerBeenOpened] = useState(false)
  const [isLoadingAttachment, setIsLoadingAttachment] = useState(false)
  const [persistedThreadId, setPersistedThreadId] = useState(null)
  const [previewContent, setPreviewContent] = useState(jsxContent)
  const [editorDiagnostics, setEditorDiagnostics] = useState([])
  const [agentPanelWidth, setAgentPanelWidth] = useState(PANEL_MIN_WIDTH)
  const [isResizingAgentPanel, setIsResizingAgentPanel] = useState(false)

  useEffect(() => {
    const stored = readStoredPanelWidth(agentId, viewScope)
    setAgentPanelWidth(stored ? clampPanelWidth(stored) : PANEL_MIN_WIDTH)
  }, [agentId, viewScope])

  const handleResizeStateChange = useCallback((resizing) => {
    setIsResizingAgentPanel(resizing)
    if (!resizing) {
      setAgentPanelWidth((current) => {
        writeStoredPanelWidth(agentId, viewScope, current)
        return current
      })
    }
  }, [agentId, viewScope])

  useImperativeHandle(ref, () => ({
    getEditorValue: () => codeViewRef.current?.getValue?.()
  }))

  const handleDiagnosticsChange = useCallback((diagnostics) => {
    setEditorDiagnostics(diagnostics)
    onDiagnosticsChangeProp?.(diagnostics)
  }, [onDiagnosticsChangeProp])

  const debouncedFormUpdate = useDebouncedCallback(
    useCallback((jsx) => {
      onJsxContentChange(jsx)
    }, [onJsxContentChange]),
    500
  )

  const handleJsxContentChange = useCallback((jsx) => {
    setPreviewContent(jsx)
    debouncedFormUpdate(jsx)
  }, [debouncedFormUpdate])

  const handleJsxUpdate = (jsx) => {
    if (onJsxContentChange) {
      onJsxContentChange(jsx)
      setPreviewContent(jsx)
      setViewMode('code')
      setTimeout(() => {
        setViewMode('preview')
      }, 100)
    }
    setIsLoadingAttachment(false)
  }

  const { threadId: restoredThreadId, isReady: restoreReady } = useRestoredThreadId(agentId, viewScope)

  useEffect(() => {
    if (!restoreReady) return
    if (restoredThreadId && !persistedThreadId) {
      setPersistedThreadId(restoredThreadId)
    }
  }, [restoreReady, restoredThreadId, persistedThreadId])

  const handleThreadIdChange = useCallback((nextThreadId) => {
    setPersistedThreadId(nextThreadId)
    if (nextThreadId) {
      writeStoredThread(agentId, viewScope, nextThreadId)
    } else {
      clearStoredThread(agentId, viewScope)
    }
  }, [agentId, viewScope])

  const hasSidePanel = aiDesignerActive || docsComponentName
  const sidePanelWidth = aiDesignerActive ? agentPanelWidth : 400
  const contentWidth = hasSidePanel ? `calc(100% - ${sidePanelWidth + 10}px)` : '100%'

  const editorContent = (
    <div
      className='relative flex-1 flex flex-col'
      style={{
        overflow: 'hidden',
        border: '5px solid var(--color-neutral-300)',
        borderRadius: 'var(--border-radius-lg)'
      }}
    >
      {isComparing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--dim-2)',
            padding: 'var(--dim-1) var(--dim-2)',
            borderBottom: '1px solid var(--color-neutral-200)',
            background: 'var(--color-neutral-050)',
            flexShrink: 0
          }}
        >
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 'var(--font-weight-bold)',
              textTransform: 'uppercase',
              color: 'var(--color-neutral-500)',
              letterSpacing: '0.05em'
            }}
          >
            Current Version
          </span>
        </div>
      )}
      <div className='relative h-full w-full flex flex-col' style={{ display: viewMode === 'code' ? 'flex' : 'none' }}>
        <CodeEditorToolbar
          diagnostics={editorDiagnostics}
          onFind={handleFind}
          onReplace={handleReplace}
          onUndo={handleUndo}
          onRedo={handleRedo}
          onFormat={handleFormat}
        />
        <div className='flex-1' style={{ minHeight: 0 }}>
          <ContentCodeView
            ref={codeViewRef}
            jsxContent={jsxContent}
            onJsxContentChange={handleJsxContentChange}
            onDiagnosticsChange={handleDiagnosticsChange}
            onComponentClick={setDocsComponentName}
          />
        </div>
        {isLoadingAttachment && (
          <div
            className='absolute flex items-center justify-center'
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10, top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div
              className='flex items-center gap-2 px-4 py-2 rounded'
              style={{ backgroundColor: 'var(--color-ai-100)', color: 'var(--color-ai-700)', border: '1px solid var(--color-ai-300)' }}
            >
              <CircularProgress color='inherit' size='18px' />
              Loading attachment...
            </div>
          </div>
        )}
      </div>

      <div className='relative h-full w-full' style={{ display: viewMode === 'preview' ? 'block' : 'none', overflow: 'auto' }}>
        {viewMode === 'preview' && <ContentPreviewView jsxContent={previewContent} />}
        {isLoadingAttachment && (
          <div
            className='absolute flex items-center justify-center'
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 10, top: 0, left: 0, right: 0, bottom: 0 }}
          >
            <div
              className='flex items-center gap-2 px-4 py-2 rounded'
              style={{ backgroundColor: 'var(--color-ai-100)', color: 'var(--color-ai-700)', border: '1px solid var(--color-ai-300)' }}
            >
              <CircularProgress color='inherit' size='18px' />
              Loading attachment...
            </div>
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className='h-full w-full flex flex-col gap-md relative'>
      <div className='flex gap-md justify-between items-center'>
        {toolbarLeft}
        <ViewContentHeaderControls
          onToggleDesigner={() => {
            if (aiDesignerActive) {
              agentPanelRef.current?.close?.()
            } else {
              setAiDesignerActive(true)
              setHasDesignerBeenOpened(true)
            }
          }}
          views={views}
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
        />
      </div>

      <div
        className='flex-1 flex gap-md'
        style={{
          overflow: 'hidden',
          width: hasSidePanel ? contentWidth : '100%',
          transition: isResizingAgentPanel ? 'none' : 'width 0.3s ease-in-out'
        }}
      >
        {isComparing && <ComparePane viewId={viewId} viewMode={viewMode} />}
        {editorContent}
      </div>

      {docsComponentName && !aiDesignerActive && (
        <div
          style={{
            position: 'absolute',
            top: '80px',
            right: 0,
            bottom: 0,
            width: '400px',
            height: 'auto',
            background: 'white',
            zIndex: 1000,
            overflow: 'hidden'
          }}
        >
          <ComponentDocsPanel
            componentName={docsComponentName}
            onClose={() => setDocsComponentName(null)}
          />
        </div>
      )}

      {hasDesignerBeenOpened && (
        <AgentSidePanel
          ref={agentPanelRef}
          agentId={agentId}
          onClose={() => setAiDesignerActive(false)}
          onJsxUpdate={handleJsxUpdate}
          onAttachmentLoadStart={() => setIsLoadingAttachment(true)}
          onThreadIdChange={handleThreadIdChange}
          onVersionCreated={onVersionCreated}
          initialThreadId={persistedThreadId}
          viewContext={isEdit ? { viewId, content: jsxContent } : undefined}
          width={agentPanelWidth}
          onWidthChange={setAgentPanelWidth}
          onResizeStateChange={handleResizeStateChange}
          isVisible={aiDesignerActive}
        />
      )}
    </div>
  )
})

ViewContentContainer.displayName = 'ViewContentContainer'

ViewContentContainer.propTypes = {
  jsxContent: PropTypes.string,
  viewId: PropTypes.number,
  onJsxContentChange: PropTypes.func,
  onDiagnosticsChange: PropTypes.func,
  toolbarLeft: PropTypes.node,
  isEdit: PropTypes.bool,
  isComparing: PropTypes.bool,
  onVersionCreated: PropTypes.func
}

export default ViewContentContainer
